// Recap — end-of-session grading + history.
//
// Reads PracticeSessionState (the Q/A pairs collected in PracticeForSession)
// from localStorage and lets the user run grade_session against the full
// transcript. The structured JSON output (overallScore + 5 axes + per-question
// summaries + soft-skill summary) is parsed via parse-grade-json and rendered
// with a simple horizontal-bar chart for axes (no extra dep).
//
// Schema borrowed from FoloUp/src/lib/prompts/analytics.ts (5 of 12 axes) +
// communication-analysis.ts supportingQuotes pattern. See plan Phase 14.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, ArrowLeft, RefreshCw, Edit3, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Progress from '../components/ui/Progress';
import Chip from '../components/ui/Chip';
import { useJobRunner } from '../lib/useJobRunner';
import { apiGet } from '../lib/api';
import { adaptDataResponse } from '../lib/adapt';
import { parseGradeJson, type GradeSessionResult, type GradeAxis } from '../lib/parse-grade-json';
import { cleanLLMOutput } from '../lib/clean-llm-output';
import type { PracticeSessionState, Application, GradeArchiveEntry } from '../lib/types';

function loadPractice(sessionId: string): PracticeSessionState | null {
  try {
    const raw = localStorage.getItem(`nice2job.practice.${sessionId}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function savePractice(sessionId: string, state: PracticeSessionState) {
  try { localStorage.setItem(`nice2job.practice.${sessionId}`, JSON.stringify(state)); }
  catch { /* noop */ }
}

function clearPractice(sessionId: string) {
  try { localStorage.removeItem(`nice2job.practice.${sessionId}`); } catch { /* noop */ }
}

function relativeTime(ts: number): string {
  const ms = Date.now() - ts;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
}

function ratingVariant(rating: string): 'good' | 'second' | 'warn' | 'default' {
  const r = rating.toLowerCase();
  if (r.includes('strong')) return 'good';
  if (r.includes('weak')) return 'warn';
  if (r.includes('adequate')) return 'second';
  return 'default';
}

export default function Recap() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();

  const dataQ = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });
  const session: Application | undefined = useMemo(() => {
    const apps = dataQ.data?.applications ?? [];
    return apps.find((a) => a.id === sessionId || String(a.num) === sessionId);
  }, [dataQ.data, sessionId]);

  const [practice, setPractice] = useState<PracticeSessionState | null>(null);
  useEffect(() => {
    if (!sessionId) return;
    setPractice(loadPractice(sessionId));
  }, [sessionId]);

  // Reattach: grade_session payload carries `application_id` = sessionId,
  // so two devices grading the same session see the same in-flight run.
  const runner = useJobRunner(`grade-session-${sessionId || 'default'}`, {
    reattach: {
      cmd: 'grade_session',
      match: (p) => !!sessionId && String(p.application_id || '') === sessionId,
    },
  });
  const cleanLog = useMemo(() => cleanLLMOutput(runner.log || ''), [runner.log]);
  const result: GradeSessionResult | null = useMemo(() => {
    if (runner.status !== 'done') return null;
    return parseGradeJson(cleanLog);
  }, [cleanLog, runner.status]);

  // ── Archive each completed grade run to gradeHistory ────────────────────
  // Dedup by jobId (each grade_session run is a new job). Defends against
  // re-archives on remount/refresh — once the same jobId is in history we
  // never re-add it. The user can re-grade as many times as they like; each
  // creates a NEW history entry so they can track progress.
  const archivedJobIdsRef = useRef<Set<string>>(new Set());
  // Hydrate the consumed set from existing history on mount so post-refresh
  // we don't double-add the previous attempt.
  useEffect(() => {
    if (!practice?.gradeHistory) return;
    for (const e of practice.gradeHistory) archivedJobIdsRef.current.add(e.id);
  }, [practice?.gradeHistory?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (runner.status !== 'done') return;
    if (!runner.jobId) return;
    if (!result) return;
    if (!sessionId) return;
    if (!practice || practice.qaPairs.length === 0) return;
    if (archivedJobIdsRef.current.has(runner.jobId)) return;
    archivedJobIdsRef.current.add(runner.jobId);

    const entry: GradeArchiveEntry = {
      id: runner.jobId,
      gradedAt: Date.now(),
      overallScore: result.overallScore,
      result,
      // Snapshot the qaPairs so user can later see WHAT answers gave this
      // score — even if they edit them later. Deep-copy via JSON to avoid
      // accidental shared refs.
      qaSnapshot: JSON.parse(JSON.stringify(practice.qaPairs)),
    };
    const next: PracticeSessionState = {
      ...practice,
      gradeHistory: [...(practice.gradeHistory ?? []), entry],
    };
    savePractice(sessionId, next);
    setPractice(next);
  }, [runner.status, runner.jobId, result, sessionId, practice]);

  const gradeHistory = practice?.gradeHistory ?? [];
  // Sort newest first for display, but compute deltas in chronological order
  const historyChrono = useMemo(() => [...gradeHistory].sort((a, b) => a.gradedAt - b.gradedAt), [gradeHistory]);
  const historyNewestFirst = useMemo(() => [...gradeHistory].sort((a, b) => b.gradedAt - a.gradedAt), [gradeHistory]);

  const goEditAnswers = () => {
    if (!sessionId) return;
    nav(`/interview/${encodeURIComponent(sessionId)}?tab=practice`);
  };

  const startGrade = () => {
    if (!practice || practice.qaPairs.length === 0) {
      alert('No Q&A pairs to grade. Practice some questions first.');
      return;
    }
    if (runner.isRunning) return;
    runner.startJob('grade_session', {
      qaPairs: practice.qaPairs.map((p) => ({ q: p.question, a: p.answer, category: p.category })),
      company: session?.company || '',
      role: session?.role || '',
      application_id: sessionId || '',
    });
  };

  const startOver = () => {
    if (!sessionId) return;
    if (!confirm('Clear all answers from this practice session?')) return;
    clearPractice(sessionId);
    setPractice(null);
    runner.dismiss();
    nav(`/interview/${encodeURIComponent(sessionId)}?tab=practice`);
  };

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav(sessionId ? `/interview/${encodeURIComponent(sessionId)}?tab=practice` : '/interview')}>
        <ArrowLeft size={12} style={{ marginRight: 4, display: 'inline' }} /> Back to practice
      </button>
      <PageHeader
        eyebrow="Practice recap"
        title={
          session ? (
            <>
              {session.company} ·{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>{session.role}</em>
            </>
          ) : 'What landed. What didn\'t.'
        }
        sub={practice ? `${practice.qaPairs.length} answer${practice.qaPairs.length === 1 ? '' : 's'} captured · started ${new Date(practice.startedAt).toLocaleString()}` : 'No practice session yet'}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Once at least one grade has landed, surface the "edit answers"
                path prominently — that's the loop user wants for tracking
                progress: revise → re-grade → compare. */}
            {gradeHistory.length > 0 && (
              <button
                className="btn"
                onClick={goEditAnswers}
                title="Go back to Practice and edit answers — your previous answers are preserved. The next grade will be a NEW history entry."
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Edit3 size={12} /> Edit answers &amp; re-grade
              </button>
            )}
            <button
              className="btn accent"
              onClick={startGrade}
              disabled={runner.isRunning || !practice || practice.qaPairs.length === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title={gradeHistory.length > 0 ? 'Run grade_session again — adds a new history entry, does not overwrite previous attempts' : ''}
            >
              <Sparkles size={13} /> {runner.isRunning ? 'Grading…' : (gradeHistory.length > 0 ? 'Re-grade now' : 'Grade this session')}
            </button>
            {practice && (
              <button className="btn ghost" onClick={startOver} title="Clear answers AND grade history">
                <RefreshCw size={12} style={{ marginRight: 4, display: 'inline' }} /> Reset
              </button>
            )}
          </div>
        }
      />

      {!practice || practice.qaPairs.length === 0 ? (
        <div className="card empty">
          <span className="serif">No answers yet for this session.</span>
          Go back to Practice and answer at least one question. The grade applies to the whole session.
        </div>
      ) : (
        <>
          {runner.isActive && !result && (
            <div className="card" style={{ padding: 22, marginBottom: 16 }}>
              <div className="tab" style={{ marginBottom: 8 }}>{runner.status === 'done' ? 'Parsing result…' : 'Grading…'}</div>
              <Progress value={Math.min(95, runner.log.length / 30)} />
              <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
                {runner.log.length.toLocaleString()} chars · this is a structured JSON grade across 5 axes — takes 60-120s.
                {runner.isRunning && (
                  <>
                    {' · '}
                    <button className="btn ghost" onClick={runner.cancel} style={{ fontSize: 10.5, padding: '1px 6px', color: '#b43a3a' }}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {runner.errorLine && (
            <div className="card" style={{ padding: 14, marginBottom: 14, borderColor: 'var(--warn)' }}>
              <div style={{ fontSize: 12, color: 'var(--warn)' }}>{runner.errorLine}</div>
            </div>
          )}

          {/* Progress / history card — shown when ≥1 grade has been archived.
              Appears ABOVE the current grade so the user immediately sees
              their trajectory before the latest detail. */}
          {historyChrono.length >= 1 && (
            <ProgressHistoryCard
              chrono={historyChrono}
              newestFirst={historyNewestFirst}
              onEditAnswers={goEditAnswers}
            />
          )}

          {result && <GradeResultView result={result} />}

          {/* Q/A list — grouped by anchor so multi-turn probe conversations
              read naturally. Each group shows anchor + N follow-up probes. */}
          {(() => {
            // Group pairs by anchor (or question for legacy rows). Preserve
            // appearance order: anchor turn first, then probes by `turn` asc
            // then by ts as fallback.
            const groups = new Map<string, typeof practice.qaPairs>();
            for (const p of practice.qaPairs) {
              const key = p.anchor || p.question;
              if (!groups.has(key)) groups.set(key, []);
              groups.get(key)!.push(p);
            }
            const totalAnchors = groups.size;
            const totalTurns = practice.qaPairs.length;
            const groupArr = Array.from(groups.entries()).map(([anchor, turns]) => {
              const sorted = [...turns].sort((a, b) =>
                ((a.turn ?? 0) - (b.turn ?? 0)) || (a.ts - b.ts),
              );
              return { anchor, turns: sorted };
            });
            return (
              <div className="card" style={{ padding: 22, marginTop: 16 }}>
                <div className="tab" style={{ marginBottom: 12 }}>
                  Captured conversations ({totalAnchors} question{totalAnchors === 1 ? '' : 's'} · {totalTurns} turn{totalTurns === 1 ? '' : 's'})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {groupArr.map((g, gi) => (
                    <div key={gi} style={{ borderLeft: '3px solid var(--pri)', paddingLeft: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <Chip variant="default" style={{ fontSize: 10 }}>{g.turns[0]?.category || 'general'}</Chip>
                        <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                          {g.turns.length} turn{g.turns.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 10, lineHeight: 1.4 }}>
                        {g.anchor}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 8, borderLeft: '1px dashed var(--line)' }}>
                        {g.turns.map((t, ti) => (
                          <div key={ti} style={{ paddingLeft: 8 }}>
                            <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginBottom: 2 }}>
                              {ti === 0 ? 'Anchor turn' : `Probe ${ti}`} · {new Date(t.ts).toLocaleTimeString()}
                            </div>
                            {ti > 0 && (
                              <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink2)', marginBottom: 3, fontStyle: 'italic' }}>
                                Q: {t.question}
                              </div>
                            )}
                            <div style={{ fontSize: 12.5, color: 'var(--ink2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {ti > 0 ? 'A: ' : ''}{t.answer}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

// ── Progress history card ────────────────────────────────────────────────
// Shown above the latest grade detail when the user has graded the session
// at least once. Lets them see score trajectory across attempts and jump
// straight back to Practice to revise.
function ProgressHistoryCard({
  chrono, newestFirst, onEditAnswers,
}: {
  chrono: GradeArchiveEntry[];
  newestFirst: GradeArchiveEntry[];
  onEditAnswers: () => void;
}) {
  const latest = newestFirst[0];
  const prev = newestFirst[1];
  const delta = prev ? latest.overallScore - prev.overallScore : 0;
  const trendIcon = delta > 0 ? <TrendingUp size={14} color="var(--good)" />
    : delta < 0 ? <TrendingDown size={14} color="var(--warn)" />
    : <Minus size={14} color="var(--ink3)" />;

  // Sparkline-ish: render each chrono attempt as a vertical bar whose
  // height is overallScore/100. Pure SVG, no chart lib. Skip if only 1 entry.
  const sparkline = chrono.length >= 2 ? (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 60, padding: '0 4px', marginTop: 8 }}>
      {chrono.map((e, i) => {
        const h = Math.max(4, (e.overallScore / 100) * 60);
        const isLatest = i === chrono.length - 1;
        const color = isLatest
          ? 'var(--pri)'
          : e.overallScore >= 75 ? 'var(--good)' : e.overallScore >= 50 ? 'var(--ink2)' : 'var(--warn)';
        return (
          <div
            key={e.id}
            title={`Attempt ${i + 1} · ${e.overallScore}/100 · ${new Date(e.gradedAt).toLocaleString()}`}
            style={{
              flex: '0 0 24px',
              height: h,
              background: color,
              borderRadius: '2px 2px 0 0',
              opacity: isLatest ? 1 : 0.7,
              position: 'relative',
            }}
          >
            <div style={{ position: 'absolute', bottom: -16, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: 'var(--ink3)' }}>
              {e.overallScore}
            </div>
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="card" style={{ padding: 22, marginBottom: 16, background: 'rgba(120, 90, 200, 0.03)', borderColor: 'rgba(120, 90, 200, 0.25)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div className="tab" style={{ flex: 1 }}>
          Progress · {chrono.length} attempt{chrono.length === 1 ? '' : 's'}
        </div>
        <button
          className="btn ghost"
          onClick={onEditAnswers}
          style={{ fontSize: 11, padding: '3px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          title="Go back to Practice — your answers are preserved. Re-grade after editing to add a new history entry."
        >
          <Edit3 size={11} /> Edit answers
        </button>
      </div>

      {/* Headline: latest score + delta from previous */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
        <div className="num" style={{ fontSize: 32, color: 'var(--pri)', lineHeight: 1 }}>
          {latest.overallScore}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink3)' }}>/ 100 latest</div>
        {prev && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: delta > 0 ? 'var(--good)' : delta < 0 ? 'var(--warn)' : 'var(--ink3)' }}>
            {trendIcon}
            {delta > 0 ? `+${delta}` : delta}
            <span style={{ color: 'var(--ink3)' }}>vs previous</span>
          </div>
        )}
      </div>

      {sparkline}
      {sparkline && <div style={{ height: 14 }} />}

      {/* Per-attempt list, newest first */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12, fontSize: 11.5 }}>
        {newestFirst.map((e, i) => {
          const attemptNum = chrono.length - i; // newest has highest number
          const earlierEntry = chrono.find((c) => c.id === newestFirst[i + 1]?.id);
          const localDelta = earlierEntry ? e.overallScore - earlierEntry.overallScore : null;
          return (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="num" style={{ minWidth: 32, color: 'var(--ink3)' }}>#{attemptNum}</span>
              <span className="num" style={{ minWidth: 36, fontSize: 13, color: 'var(--ink)' }}>
                {e.overallScore}
              </span>
              <span style={{ color: 'var(--ink3)', minWidth: 70 }}>
                {relativeTime(e.gradedAt)}
              </span>
              <span style={{ color: 'var(--ink3)', flex: 1, fontSize: 11 }}>
                {e.qaSnapshot.length} turn{e.qaSnapshot.length === 1 ? '' : 's'}
              </span>
              {localDelta != null && (
                <span style={{ fontSize: 10.5, color: localDelta > 0 ? 'var(--good)' : localDelta < 0 ? 'var(--warn)' : 'var(--ink3)' }}>
                  {localDelta > 0 ? `+${localDelta}` : localDelta}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Result view ──────────────────────────────────────────────────────────
function GradeResultView({ result }: { result: GradeSessionResult }) {
  const overallColor = result.overallScore >= 75 ? 'var(--good)' : result.overallScore >= 50 ? 'var(--ink)' : 'var(--warn)';
  return (
    <>
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <div className="tab" style={{ marginBottom: 8 }}>Overall</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <div className="num" style={{ fontSize: 64, lineHeight: 1, color: overallColor }}>
            {result.overallScore}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink3)' }}>/ 100</div>
        </div>
        <Progress value={result.overallScore} color={overallColor} style={{ marginTop: 14 }} />
        {result.overallFeedback && (
          <div style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.65, marginTop: 14 }}>
            {result.overallFeedback}
          </div>
        )}
        {result.softSkillSummary && (
          <div style={{ fontSize: 11.5, color: 'var(--ink3)', fontStyle: 'italic', marginTop: 8 }}>
            Soft skill read: {result.softSkillSummary}
          </div>
        )}
      </div>

      {/* Axes — horizontal bars, no charting library */}
      {result.axes.length > 0 && (
        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <div className="tab" style={{ marginBottom: 12 }}>Per-axis breakdown (0-10)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {result.axes.map((axis, i) => <AxisBar key={i} axis={axis} />)}
          </div>
        </div>
      )}

      {/* Per-question table */}
      {result.questionSummaries.length > 0 && (
        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <div className="tab" style={{ marginBottom: 12 }}>Per-question</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {result.questionSummaries.map((q, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Chip variant={ratingVariant(q.rating)} style={{ fontSize: 10, minWidth: 64, textAlign: 'center', flexShrink: 0 }}>
                  {q.rating}
                </Chip>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 3 }}>
                    {q.question}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.6 }}>
                    {q.summary}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function AxisBar({ axis }: { axis: GradeAxis }) {
  const color = axis.score >= 7 ? 'var(--good)' : axis.score >= 5 ? 'var(--ink)' : 'var(--warn)';
  const pct = Math.max(0, Math.min(10, axis.score)) * 10;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', minWidth: 130 }}>{axis.name}</div>
        <div className="num" style={{ fontSize: 14, color, minWidth: 28, textAlign: 'right' }}>
          {axis.score}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink3)' }}>/ 10</div>
        <div style={{ flex: 1, marginLeft: 6 }}>
          <Progress value={pct} color={color} />
        </div>
      </div>
      {axis.feedback && (
        <div style={{ fontSize: 11.5, color: 'var(--ink2)', marginTop: 4, marginLeft: 138, lineHeight: 1.55 }}>
          {axis.feedback}
        </div>
      )}
      {axis.quote && (
        <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4, marginLeft: 138, fontStyle: 'italic', borderLeft: '2px solid var(--line2)', paddingLeft: 8 }}>
          "{axis.quote}"
        </div>
      )}
    </div>
  );
}
